// Notifications inbox (04 §5.17). Payloads were built for the recipient at write time.
import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from './common.js';

export const NOTIFICATION_PAGE_DEFAULT = 50;
export const NOTIFICATION_PAGE_MAX = 200;

/** Inbox tabs (G-80), in display order. The category follows from the type. */
export const NOTIFICATION_CATEGORIES = [
  'combat',
  'economy',
  'intel',
  'alliance',
  'account',
] as const;
export const notificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);
export type NotificationCategory = z.infer<typeof notificationCategorySchema>;

/** Listed types of the categories that are not `alliance` (every `ALLIANCE_*`) or `account` (the rest). */
export const NOTIFICATION_TYPES_BY_CATEGORY = {
  combat: [
    'FLEET_INCOMING',
    'FLEET_INCOMING_CANCELLED',
    'FLEET_ORDER_FAILED',
    'FLEET_ORDER_RESCHEDULED',
    'BATTLE_STARTED',
    'BATTLE_ENDED',
    'CLAIM_PLACED',
    'CLAIM_ACTIVATED',
    'CLAIM_RESOLVED',
    'SYSTEM_OWNER_CHANGED',
  ],
  /** The routine notices a player can switch off (G-82) and that are cleaned up when read (G-83). */
  economy: ['BUILDING_COMPLETED', 'SHIP_PRODUCED', 'PORT_TRADE_COMPLETED'],
  intel: ['SCOUT_RETURNED', 'SCOUT_FAILED_AGAINST_YOU'],
} as const satisfies Partial<Record<NotificationCategory, readonly string[]>>;

export const ALLIANCE_NOTIFICATION_PREFIX = 'ALLIANCE_';

export function notificationCategory(type: string): NotificationCategory {
  for (const [category, types] of Object.entries(NOTIFICATION_TYPES_BY_CATEGORY)) {
    if ((types as readonly string[]).includes(type)) return category as NotificationCategory;
  }
  return type.startsWith(ALLIANCE_NOTIFICATION_PREFIX) ? 'alliance' : 'account';
}

export const notificationSchema = z.strictObject({
  id: idSchema,
  type: z.string(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: isoDateTimeSchema,
  readAt: isoDateTimeSchema.nullable(),
});
export type Notification = z.infer<typeof notificationSchema>;

export const notificationListQuerySchema = z.strictObject({
  unread: z.enum(['true', 'false']).optional(),
  type: z.string().min(1).max(64).optional(),
  category: notificationCategorySchema.optional(),
  /** Only notifications older than this one (ids are time-ordered UUIDv7). */
  before: idSchema.optional(),
  limit: z.coerce.number().int().min(1).max(NOTIFICATION_PAGE_MAX).optional(),
});

const countsByCategorySchema = z.strictObject({
  combat: z.number().int().nonnegative(),
  economy: z.number().int().nonnegative(),
  intel: z.number().int().nonnegative(),
  alliance: z.number().int().nonnegative(),
  account: z.number().int().nonnegative(),
});
export type NotificationCountsByCategory = z.infer<typeof countsByCategorySchema>;

export const notificationListResponseSchema = z.strictObject({
  items: z.array(notificationSchema),
  unreadCount: z.number().int().nonnegative(),
  unreadByCategory: countsByCategorySchema,
});
export type NotificationListResponse = z.infer<typeof notificationListResponseSchema>;

export const markNotificationsReadRequestSchema = z.union([
  z.strictObject({ ids: z.array(idSchema).min(1).max(NOTIFICATION_PAGE_MAX) }),
  z.strictObject({ all: z.literal(true), category: notificationCategorySchema.optional() }),
]);
export type MarkNotificationsReadRequest = z.infer<typeof markNotificationsReadRequestSchema>;

export const markNotificationsReadResponseSchema = z.strictObject({
  unreadCount: z.number().int().nonnegative(),
});

/** G-81: delete notices by id (read or not), or every read one, optionally of one tab. */
export const deleteNotificationsRequestSchema = z.union([
  z.strictObject({ ids: z.array(idSchema).min(1).max(NOTIFICATION_PAGE_MAX) }),
  z.strictObject({ read: z.literal(true), category: notificationCategorySchema.optional() }),
]);
export type DeleteNotificationsRequest = z.infer<typeof deleteNotificationsRequestSchema>;

export const deleteNotificationsResponseSchema = z.strictObject({
  deleted: z.number().int().nonnegative(),
  unreadCount: z.number().int().nonnegative(),
});
export type DeleteNotificationsResponse = z.infer<typeof deleteNotificationsResponseSchema>;

/** Every type a player can switch off (G-82, changed 2026-10-24), per inbox tab in display order. */
export const NOTIFICATION_SETTING_TYPES = {
  ...NOTIFICATION_TYPES_BY_CATEGORY,
  alliance: [
    'ALLIANCE_INVITE_RECEIVED',
    'ALLIANCE_APPLICATION_RECEIVED',
    'ALLIANCE_APPLICATION_ACCEPTED',
    'ALLIANCE_RANK_CHANGED',
    'ALLIANCE_KICKED',
    'ALLIANCE_DISBANDED',
  ],
  account: ['SHIELD_WARNING', 'SHIELD_EXPIRED'],
} as const satisfies Record<NotificationCategory, readonly string[]>;

export const SWITCHABLE_NOTIFICATION_TYPES = Object.values(NOTIFICATION_SETTING_TYPES).flat();
export const switchableNotificationTypeSchema = z.enum(
  SWITCHABLE_NOTIFICATION_TYPES as [string, ...string[]]
);

/** Account-wide inbox settings (G-82, slice subset of `notification_settings`): the types switched off. */
export const notificationSettingsSchema = z.strictObject({
  disabledTypes: z
    .array(switchableNotificationTypeSchema)
    .max(SWITCHABLE_NOTIFICATION_TYPES.length)
    .refine((types) => new Set(types).size === types.length, 'Each type at most once.'),
});
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;
export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = { disabledTypes: [] };
